import { type FC } from "react";

import { Download, Users } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/utils/core";
import {
  ADMIN_FORM_NAMES,
  ADMIN_FORM_URLS,
} from "@/shared/constants/adminForms";

import type { AdminFormId } from "@/shared/types/administrationTypes";

import formDocumentIcon from "@/shared/assets/probate/form-document.svg";

interface FormRowProps {
  formId: AdminFormId;
  signerLabel: string;
  isPersonSigner: boolean;
  citedPersons?: string[];
  noticeRecipients?: string[];
}

export const FormRowItem: FC<FormRowProps> = ({ formId, signerLabel, isPersonSigner, citedPersons, noticeRecipients }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-white p-3 sm:p-4 border-b border-primary last:border-b-0">
    <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
      <img src={formDocumentIcon} alt="" className="size-5 sm:size-8 shrink-0 mt-0.5 sm:mt-0" aria-hidden="true" />
      <div className="flex flex-1 flex-col min-w-0 justify-center">
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <span className="text-sm font-medium leading-[1.55]">{ADMIN_FORM_NAMES[formId]}</span>
          <span className="border border-primary rounded-sm px-1 py-0.5 font-mono text-xs text-secondary shrink-0 leading-[1.55]">
            Form {formId}
          </span>
        </div>
        <span className={cn("text-[13px] leading-[1.55]", isPersonSigner ? "font-medium text-brand" : "font-normal text-secondary")}>
          {signerLabel}
        </span>
        {citedPersons && citedPersons.length > 0 && (
          <span className="text-xs text-secondary mt-0.5">
            {citedPersons.join(", ")}
          </span>
        )}
        {noticeRecipients && noticeRecipients.length > 0 && (
          <div className="flex flex-col gap-1 mt-1">
            {noticeRecipients.map((person, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Users className="size-3.5 text-secondary shrink-0" aria-hidden="true" />
                <span className="text-[13px] text-secondary">{person}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    <Button
      variant="outline"
      className="shrink-0 gap-2 self-start sm:self-center ml-8 sm:ml-0"
      aria-label={`Download ${formId} PDF`}
      onClick={() => window.open(ADMIN_FORM_URLS[formId], "_blank")}
    >
      <Download className="size-4" aria-hidden="true" />
      PDF
    </Button>
  </div>
);
